import React from 'react';
import LandingHeader from '../../ui/LandingHeader';
import LandingFooter from '../../ui/LandingFooter';
import CallToAction from '../CallToAction';

const ForClientFacingDashboardsPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-background antialiased">
            <div className="relative z-10">
                <LandingHeader />
            </div>
            <main className="relative">
                {/* Hero Section */}
                <section className="pt-20 md:pt-32 pb-16 md:pb-24 px-4 relative overflow-hidden">
                    {/* Decorative elements */}
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute top-0 left-1/4 w-48 md:w-72 h-48 md:h-72 bg-primary/5 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 right-1/4 w-48 md:w-72 h-48 md:h-72 bg-primary/5 rounded-full blur-3xl" />
                    </div>
                    
                    <div className="container mx-auto max-w-5xl">
                        <div className="text-center space-y-4 md:space-y-6">
                            <div className="inline-flex items-center px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-primary/10 text-primary text-xs md:text-sm font-medium mb-2 md:mb-4">
                                <svg className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                Use Case: Client-Facing Dashboards
                            </div>
                            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground">
                                Deliver Stunning Dashboards<br className="hidden md:block" />
                                <span className="text-primary">Without the Backend</span>
                            </h1>
                            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                                Create branded, interactive dashboards that run entirely in the browser. 
                                Share client insights securely and instantly—no servers, no logins, no infrastructure.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Features Grid */}
                <section className="py-16 md:py-24 bg-muted/50 relative">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="container mx-auto max-w-5xl px-2 sm:px-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
                            <div className="space-y-6 md:space-y-8">
                                <div className="flex items-start space-x-3 md:space-x-4">
                                    <div className="bg-primary/10 p-2 md:p-3 rounded-lg text-primary shrink-0">
                                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg md:text-xl font-semibold mb-2">Brand-Ready Outputs</h3>
                                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                            Customize logos, colors, and copy directly in Python or config files. 
                                            Perfect for maintaining consistent branding across client deliverables.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3 md:space-x-4">
                                    <div className="bg-primary/10 p-2 md:p-3 rounded-lg text-primary shrink-0">
                                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg md:text-xl font-semibold mb-2">One-File to Web App</h3>
                                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                            Combine logic, layout, and interactivity in a single Python script. 
                                            Export as a static web app that works anywhere, anytime.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3 md:space-x-4">
                                    <div className="bg-primary/10 p-2 md:p-3 rounded-lg text-primary shrink-0">
                                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg md:text-xl font-semibold mb-2">No Login Required</h3>
                                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                            Distribute as static folders, email as zip, or embed anywhere. 
                                            Clients get instant access without any setup or authentication.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-card rounded-lg border shadow-sm p-4 md:p-6 relative mt-8 md:mt-0">
                                <div className="absolute -top-3 -right-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Live Example
                                </div>
                                <pre className="text-xs md:text-sm text-muted-foreground font-mono overflow-x-auto">
                                    <code>{`# Quarterly Marketing Dashboard
import preswald as pw
import pandas as pd

# Load client-specific data
df = pd.read_csv('client_metrics.csv')
branding = {
    'logo': 'client_logo.png',
    'colors': ['#1E40AF', '#3B82F6']
}

# Create interactive dashboard
app = pw.App("Q2 Marketing Report")

@app.page("Overview")
def overview():
    return pw.Column([
        pw.Header(branding['logo']),
        pw.Chart(df, type="line"),
        pw.Dropdown("Select Campaign", 
                   options=df['campaign'].unique()),
        pw.KPI("ROI", value="245%"),
        pw.KPI("Conversion", value="3.2%")
    ])

# Export as static app
app.export("client_dashboard.html")`}</code>
                                </pre>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Dashboard Types */}
                <section className="py-16 md:py-24">
                    <div className="container mx-auto max-w-5xl px-4">
                        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 md:mb-16">Common Dashboard Types</h2>
                        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3">Campaign Performance</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Track ROI, conversions, and engagement metrics across marketing campaigns.
                                </p>
                            </div>

                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3">Customer Health</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Monitor customer satisfaction, churn risk, and engagement scores.
                                </p>
                            </div>

                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3">Usage & Engagement</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Track feature adoption, user activity, and platform engagement.
                                </p>
                            </div>

                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3">Risk & Compliance</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Monitor security metrics, compliance status, and risk indicators.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Benefits */}
                <section className="py-16 md:py-24 bg-muted/50 relative">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="container mx-auto max-w-5xl px-4">
                        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 md:mb-16">Why Teams Choose Preswald</h2>
                        <div className="grid md:grid-cols-2 gap-6 md:gap-12">
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 relative">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    For Your Team
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">Development Benefits</h3>
                                <ul className="space-y-3 md:space-y-4 text-muted-foreground">
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Eliminate dashboard backlog and dev bottlenecks</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Iterate quickly and re-export with updated data</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">No server maintenance or deployment cost</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Works with your existing Python workflow</span>
                                    </li>
                                </ul>
                            </div>
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 relative">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    For Your Clients
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">Client Benefits</h3>
                                <ul className="space-y-3 md:space-y-4 text-muted-foreground">
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Clean, interactive reports—no Tableau/PowerBI license needed</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Viewable offline or from any browser</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">No logins, frictionless access</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Professional, branded experience</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                <CallToAction />
            </main>
            <LandingFooter />
        </div>
    );
};

export default ForClientFacingDashboardsPage; 